import { friesStatus, pattyDoneness, pattyStatus, type CookStatus } from "../game/food";
import { machineStatus, type ShiftState } from "../game/shift";

// What to play, worked out from how the shift changed between two frames.
// Pure, so the tests can say which actions make which sounds; `sfx.ts` turns
// cues into noise.

export type Cue =
  /** A new ticket printed at the pass. */
  | "ticket"
  /** A dish lifted out of the tub. */
  | "pickup"
  /** A dish set back down in the tub. */
  | "putBack"
  /** A dish dropped into the sink water. */
  | "splash"
  /** A pass of the sponge. */
  | "scrub"
  /** The last of the grime came off. */
  | "clean"
  /** A dish set into the rack. */
  | "rack"
  /** The hood comes down and the cycle starts. */
  | "machineStart"
  /** The cycle's done. */
  | "machineDone"
  /** The clean rack lifted out of the machine. */
  | "rackOut"
  /** Clean dishes stacked onto the shelf. */
  | "shelve"
  /** An order went out to a table. */
  | "served"
  | "walkout"
  /** A dish hit the floor. */
  | "crash"
  /** The shift's over. */
  | "closing"
  /** A raw patty hits the hot flat-top. */
  | "sizzle"
  /** A patty flipped with the spatula. */
  | "flip"
  /** A patty or a basket set down on the board, or a clean basket taken. */
  | "thunk"
  /** Fries dropped into hot oil. */
  | "fryDrop"
  /** Fries lifted out of the oil. */
  | "fryLift"
  /** A topping on or off. */
  | "topping"
  /** The order-up bell at the window. */
  | "bell"
  /** Food on the heat needs you: a patty to flip, or a patty or fries done. */
  | "ready"
  /** Food on the heat is about to burn. */
  | "hurry"
  /** Something burnt. */
  | "burnt"
  /** Food in the trash. */
  | "trash";

export function cuesFor(prev: ShiftState, next: ShiftState): Cue[] {
  if (prev === next) return [];
  const cues: Cue[] = [];
  const seen = new Set(prev.tickets.map((t) => t.id));
  if (next.tickets.some((t) => !seen.has(t.id))) cues.push("ticket");

  if (!prev.held && next.held?.kind === "dish") cues.push("pickup");
  if (prev.held?.kind === "dish" && prev.held.dish.grime > 0 && !next.held) cues.push(next.sink?.id === prev.held.dish.id || next.rack.length > prev.rack.length ? "splash" : "putBack");

  if (prev.sink && next.sink?.id === prev.sink.id && next.sink.grime < prev.sink.grime) cues.push("scrub");
  const racked = next.rack.length > prev.rack.length;
  const cleaned = prev.sink && prev.sink.grime > 0 && (!next.sink || next.sink.id !== prev.sink.id || next.sink.grime === 0);
  if (cleaned) cues.push("scrub", "clean");
  if (racked) cues.push("rack");

  const before = machineStatus(prev);
  const after = machineStatus(next);
  if (before === "idle" && after === "washing") cues.push("machineStart");
  if (before === "washing" && after === "done") cues.push("machineDone");
  if (prev.held?.kind !== "rack" && next.held?.kind === "rack") cues.push("rackOut");
  if (prev.held && !next.held && (prev.held.kind === "rack" || (prev.held.kind === "dish" && prev.held.dish.grime === 0))) cues.push("shelve");

  // Cooking.
  const laid = next.griddle.some((p, i) => p && !prev.griddle[i]);
  const flipped = next.griddle.some((p, i) => {
    const was = prev.griddle[i];
    return p && was && p.sides[0] === was.sides[1] && p.sides[1] === was.sides[0] && p.sides[0] !== was.sides[0];
  });
  const offGriddle = prev.griddle.some((p, i) => p && !next.griddle[i]);
  const dropped = next.fryer.some((f, i) => f && !prev.fryer[i]);
  const lifted = prev.fryer.some((f, i) => f && !next.fryer[i]);
  // Each board spot: food into the basket there (or into a fresh one set down
  // for it), baskets swapped, a basket set down or picked up, a topping.
  let ontoBoard = false;
  let boardMoved = false;
  let topped = false;
  next.board.forEach((n, i) => {
    const p = prev.board[i] ?? null;
    if (!p !== !n || (p && n && p.dish.id !== n.dish.id)) boardMoved = true;
    if (!n) return;
    const pb = p?.dish.id === n.dish.id ? p.contents : null;
    const nb = n.contents;
    if (pb ? nb.patties.length > pb.patties.length || (!pb.fries && !!nb.fries) : nb.patties.length > 0 || !!nb.fries) ontoBoard = true;
    if (pb && pb.toppings.length !== nb.toppings.length) topped = true;
  });
  const ph = prev.held?.kind === "basket" ? prev.held.basket : null;
  const nh = next.held?.kind === "basket" ? next.held.basket : null;
  const swapped = !!ph && !!nh && ph.dish.id !== nh.dish.id;
  const heldTopped = !!ph && !!nh && !swapped && ph.contents.toppings.length !== nh.contents.toppings.length && ph.contents.patties === nh.contents.patties;
  if (heldTopped) topped = true;
  const down = (s: ShiftState) => s.board.filter(Boolean).length;
  const basketBack = !!ph && !next.held && down(prev) === down(next) && next.stats.served === prev.stats.served;
  const basketTaken = !!nh && !ph && down(prev) === down(next);
  if (laid) cues.push("sizzle");
  if (flipped) cues.push("flip");
  if (offGriddle) cues.push(next.held?.kind === "patty" && pattyDoneness(next.held.patty) === "burnt" ? "trash" : "flip");
  if (dropped) cues.push("fryDrop");
  if (lifted) cues.push("fryLift");
  if (topped) cues.push("topping");
  if (boardMoved || ontoBoard || basketBack || basketTaken) cues.push("thunk");
  // The heat moving food on: a ding when it needs you, beeps before it burns.
  const now: CookStatus[] = [];
  const was: CookStatus[] = [];
  next.griddle.forEach((p, i) => {
    const before = prev.griddle[i];
    if (!p || !before) return;
    now.push(pattyStatus(p).status);
    was.push(pattyStatus(before).status);
  });
  next.fryer.forEach((f, i) => {
    const before = prev.fryer[i];
    if (!f || !before) return;
    now.push(friesStatus(f).status);
    was.push(friesStatus(before).status);
  });
  // Shots and milk heat like fries.
  next.groups.forEach((g, i) => {
    const before = prev.groups[i];
    if (!g || !before) return;
    now.push(friesStatus(g).status);
    was.push(friesStatus(before).status);
  });
  if (next.wand && prev.wand) {
    now.push(friesStatus(next.wand).status);
    was.push(friesStatus(prev.wand).status);
  }
  const became = (status: CookStatus) => now.some((n, i) => n === status && was[i] !== status);
  if (became("flip") || became("done")) cues.push("ready");
  if (became("burning")) cues.push("hurry");
  if (next.events.some((e) => e.kind === "burnt" && !prev.events.some((p) => p.id === e.id))) cues.push("burnt");
  // Food out of your hands that didn't go anywhere went in the trash; a basket that lost its food was dumped.
  const food = prev.held?.kind === "patty" || prev.held?.kind === "fries";
  if (food && !next.held && !laid && !dropped && !ontoBoard) cues.push("trash");
  // Done food off the heat into the basket in your hands.
  const scooped = prev.held?.kind === "basket" && next.held?.kind === "basket" && (offGriddle || lifted);
  if (prev.held?.kind === "basket" && next.held?.kind === "basket" && prev.held.basket.contents !== next.held.basket.contents && !scooped && !swapped && !heldTopped) cues.push("trash");
  if ((prev.held?.kind === "basket" || prev.held?.kind === "cup" || prev.held?.kind === "coffee") && !next.held && next.stats.served > prev.stats.served) cues.push("bell");
  // The coffee truck: a portafilter locked in or a pitcher on the wand, shots and milk into the cup, syrup pumps.
  if (next.groups.some((g, i) => g && !prev.groups[i]) || (!prev.wand && next.wand)) cues.push("thunk");
  if (prev.groups.some((g, i) => g && !next.groups[i]) || (prev.wand && !next.wand)) cues.push("pickup");
  const pc = prev.bar;
  const nc = next.bar;
  if (nc && (nc.shots.length > (pc?.shots.length ?? 0) || (!!nc.milk && !pc?.milk))) cues.push("splash");
  if (pc && nc && pc.syrups.length !== nc.syrups.length) cues.push("topping");
  // Cups: picked up, set on the ledge or back on the shelf, tipped out.
  if (next.held?.kind === "cup" && prev.held?.kind !== "cup") cues.push("pickup");
  if (prev.held?.kind === "cup" && !next.held && next.stats.served === prev.stats.served) cues.push("thunk");
  if (prev.held?.kind === "cup" && prev.held.cup.drink && next.held?.kind === "dish") cues.push("splash");

  if (next.stats.served > prev.stats.served) cues.push("served");
  if (next.stats.walkouts > prev.stats.walkouts) cues.push("walkout");
  if (next.stats.breaks > prev.stats.breaks) cues.push("crash");
  // The closing bell when the doors shut, and the machine's chime when the last dish is put away.
  if (prev.t < prev.setup.shiftSeconds && next.t >= next.setup.shiftSeconds) cues.push("closing");
  if (!prev.over && next.over) cues.push("machineDone");
  return cues;
}
